import { FunctionComponent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { message, Space, Tooltip } from 'antd';
import classNames from 'classnames';
import i18next from 'i18next';
import { EnvService } from '@hub/services/core/env-service';
import { trackMixpanel } from '@monorepo/client-common';
import { Icons } from '@monorepo/icons';
import { Button, Modal, ModalHandle } from '@monorepo/react-components';
import {
  LoadingState,
  useLoading,
  useResponsiveness,
} from '@monorepo/react-hooks';
import {
  Account,
  EventTypes,
  IconsNames,
  MailOtpSource,
  PayoutSource,
  WithdrawBlockReason,
  WithdrawRulesResponse,
  WithdrawRulesType,
} from '@monorepo/types';
import { MailConfirmation } from '../../components/mail-confirmation/mail-confirmation';
import { useStore } from '../../helpers/use-store';
import { PayoutRequestForm } from './payout-request-form';

import './withdraw-funds.scss';

interface Props {
  maxWithdrawal: number;
  minWithdrawal: number;
  credits: number;
  userPayoutPercentage: number;
  reason?: WithdrawBlockReason;
  showAmountSelection?: boolean;
  disabled?: boolean;
  sourceId: string;
  displayName: string;
  tsAccountId?: string;
  source: PayoutSource;
  validateWithDrawEligible: () => Promise<WithdrawRulesResponse>;
  className?: string;
  account?: Account;
}

const createTooltip = (reason: WithdrawBlockReason) => {
  const RuleToText: Partial<Record<WithdrawRulesType, string>> = {
    [WithdrawRulesType.MinimumAmount]: i18next.t(
      'withdrawFunds:tooltip.minimum_amount',
      {
        ruleValue: reason?.ruleValue,
        currentValue: reason?.currentValue?.toFixed(2),
      },
    ),
    [WithdrawRulesType.MinimumPayoutCoolDown]: reason?.currentValue
      ? i18next.t('withdrawFunds:tooltip.minimum_payout_current_value', {
          currentValue: reason?.currentValue,
          ruleValue: reason?.ruleValue,
        })
      : i18next.t('withdrawFunds:tooltip.minimum_payout'),
    [WithdrawRulesType.MinimumBalance]: i18next.t(
      'withdrawFunds:tooltip.minimum_balance',
      {
        currentValue: reason?.currentValue?.toFixed(2),
        ruleValue: reason?.ruleValue,
      },
    ),
    [WithdrawRulesType.MinimumVesting]: i18next.t(
      'withdrawFunds:tooltip.minimum_vesting',
      {
        ruleValue: reason?.ruleValue,
        currentValue: reason?.currentValue,
      },
    ),
    [WithdrawRulesType.OpenPositions]: i18next.t(
      'withdrawFunds:tooltip.open_positions',
    ),
  };

  return RuleToText[reason.rule];
};

const WithdrawFundsContent: FunctionComponent<Props> = ({
  disabled,
  reason,
  displayName,
  tsAccountId,
  validateWithDrawEligible,
  source,
  showAmountSelection,
  maxWithdrawal,
  sourceId,
  userPayoutPercentage,
  credits,
  className,
  account,
}) => {
  const { isLoading, updateLoadingState } = useLoading();
  const { isMobile } = useResponsiveness();
  const { t } = useTranslation([
    'mailConfirmation',
    'common',
    'errors',
    'withdrawFunds',
  ]);

  const {
    dataStore: { accountStore, userStore },
  } = useStore();

  const reasonTooltip = reason ? createTooltip(reason) : '';

  const handleWithdrawClick = async () => {
    if (source === PayoutSource.Account && tsAccountId) {
      try {
        updateLoadingState(LoadingState.Loading);
        await accountStore.sync(tsAccountId, true);
        const { isValid } = await validateWithDrawEligible();
        if (!isValid) {
          updateLoadingState(LoadingState.Error);
          message.error(t('errors:error_refresh'), 3);
          return;
        }
        updateLoadingState(LoadingState.Loaded);
      } catch (e) {
        console.error('Failed to sync account', e);
        updateLoadingState(LoadingState.Error);
        message.error(t('errors:error_refresh'), 3);

        return;
      }
    }

    trackMixpanel({
      event: EventTypes.WITHDRAW_MODAL_OPENED,
      properties: {
        email: userStore.currentUser.email,
        platform: EnvService.get().application,
        programId: account?.programId,
        orderId: account?.purchaseId,
      },
    });

    modalRef.current?.open();
  };

  const modalRef = useRef<ModalHandle>(null);

  return (
    <>
      <Modal
        width={isMobile ? 342 : 650}
        ref={modalRef}
        footer={null}
        destroyOnClose
      >
        <MailConfirmation
          verifiedCodeComponent={
            <PayoutRequestForm
              maxWithdrawal={maxWithdrawal}
              showAmountSelection={showAmountSelection}
              source={source}
              sourceId={sourceId}
              modalRef={modalRef}
              displayName={displayName}
              credits={credits}
              userPayoutPercentage={userPayoutPercentage}
              validateWithDrawEligible={validateWithDrawEligible}
            />
          }
          sendCodeStageHeader={t('mailConfirmation:confirm_withdrawal_header')}
          sendCodeStageMessage={t(
            'mailConfirmation:confirm_withdrawal_message',
          )}
          source={source as unknown as MailOtpSource}
          sourceId={sourceId}
          displayName={displayName}
        />
      </Modal>
      <Space direction={'horizontal'}>
        <Tooltip title={reasonTooltip}>
          <Button
            shape={'round'}
            className={classNames('withdraw-funds-button', className)}
            icon={<Icons iconName={IconsNames.Cash} />}
            disabled={disabled}
            onClick={handleWithdrawClick}
            loading={isLoading}
          >
            {t('common:withdraw')}
          </Button>
        </Tooltip>
      </Space>
    </>
  );
};

export default WithdrawFundsContent;
